import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '../../networking/client';
import { RootState } from '../store';
import { toast } from 'react-toastify';

export interface ConsultationInvestigation {
  consultation_investigations_id: number;
  consultation_investigations_name: string;
  consultation_investigations_description: string;
  service_id: number;
}

export interface ConsultationInvestigationsState {
  status: string;
  editingStatus: string;
  consultationInvestigations: ConsultationInvestigation[];
}

const initialState: ConsultationInvestigationsState = {
  status: 'idle',
  editingStatus: 'idle',
  consultationInvestigations: [],
};

export const fetchConsultationInvestigations = createAsyncThunk(
  'consultationInvestigations/fetchAll',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const consultationInvestigationsResponse: any = await client.get(
        '/healthpoint/consultation-investigations'
      );
      return consultationInvestigationsResponse;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

type EditConsultationInvestigation = {
  consultationsInvestigationDescription: string;
  consultationInvestigationId: number;
};

export const editConsultationInvestigation = createAsyncThunk(
  'consultationInvestigation/edit',
  async (
    editConsultationInvestigation: EditConsultationInvestigation,
    { dispatch, rejectWithValue }
  ) => {
    try {
      await client.put(
        `/healthpoint/consultation-Investigation/${editConsultationInvestigation.consultationInvestigationId}`,
        {
          consultationsInvestigationDescription:
            editConsultationInvestigation.consultationsInvestigationDescription,
        }
      );
      dispatch(fetchConsultationInvestigations());
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const consultationInvestigationsSlice = createSlice({
  name: 'consultationInvestigations',
  initialState,
  reducers: {
    resetConsultationInvestigations: (_) => {
      return initialState;
    },
    resetDeletingStatus: (state) => {
      return {
        ...state,
        deletingStatus: 'idle',
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConsultationInvestigations.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(fetchConsultationInvestigations.fulfilled, (state, action) => {
        state.consultationInvestigations = action.payload.data.consultationInvestigations;
        state.status = 'fulfilled';
      })
      .addCase(fetchConsultationInvestigations.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.status = 'rejected';
      })
      .addCase(editConsultationInvestigation.pending, (state) => {
        state.editingStatus = 'pending';
      })
      .addCase(editConsultationInvestigation.fulfilled, (state) => {
        state.editingStatus = 'fulfilled';
        toast.success('Successfully updated the investigation.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      })
      .addCase(editConsultationInvestigation.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.editingStatus = 'rejected';
      });
  },
});

export const { resetConsultationInvestigations, resetDeletingStatus } =
  consultationInvestigationsSlice.actions;

export default consultationInvestigationsSlice.reducer;

export const selectConsultationInvestigations = (state: RootState) =>
  state.consultationInvestigations.consultationInvestigations;
export const selectConsultationInvestigationStatus = (state: RootState) =>
  state.consultationInvestigations.status;
export const selectConsultationInvestigationEditingStatus = (state: RootState) =>
  state.consultationInvestigations.editingStatus;
